import { BasicOverlay, theme, typographyRem } from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import React, { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { AppFeatures } from '../components/AppFeatures'
import { ContactUsWrapper } from '../components/ContactUsWrapper'
import { ExplainerModalSection } from '../components/ExplainerModalSection'
import { LottieAnimationExplainer } from '../components/LottieAnimationExplainer/LottieAnimationExplainer'
import { MilesTracker } from '../components/MilesTracker'
import { DifficultPlugin, WhyPluginOverlay } from '../components/Overlays'
import { GenericOverlay } from '../components/Overlays'
import { PluginTrackerOverlay } from '../components/Overlays/PluginTrackerOverlay'
import { PayByMileExplainer } from '../components/PayByMileExplainer'
import { PMBCarouselExplainer } from '../components/PBMCarouselExplainer'
import { SectionTitle } from '../components/SectionTitle'
import { PaymentType } from '../generated/graphql'
import { BasePage, BasePageProps } from '../HOCs/BasePage'
import { updateQuoteMutation } from '../hooks'
import { ProviderContext } from '../Provider'
import { getExperimentVariant } from '../utils/getExperimentVariant'
import { handleQuoteProgress } from '../utils/handleQuoteProgress'
import { HowStyles } from './styles/HowPageStyles'
const { hero: Hero, paragraphBody: ParagraphBody, paragraphLead: ParagraphLead } = typographyRem

const How = ({ quote }: BasePageProps) => {
  const history = useHistory()
  const { state } = useContext(ProviderContext)
  const { isTrackerless, activeModal, showModal } = state
  const isRefundAtRenewal = quote?.details.policy?.paymentType === PaymentType.RefundAtRenewalYearly

  const { updateQuote } = updateQuoteMutation()

  useEffect(() => {
    const target = window.location.pathname
    const status = quote?.status?.sections
    const progress = handleQuoteProgress(status, target)
    if (target !== progress) {
      history.push(progress)
    }
  }, [])

  useEffect(() => {
    if (state.trackerConfirmed !== quote?.details.site?.trackerConfirmed) {
      updateQuote({
        variables: {
          id: state.quoteId,
          actions: [
            {
              trackerConfirmed: {
                confirmed: state.trackerConfirmed,
              },
            },
          ],
        },
      })
    }
  }, [state.trackerConfirmed])

  const bmmbHowExperimentVariant = getExperimentVariant('BMMBCheckoutHowExperiment', quote?.serverSideAllocation)
  const monthlyHowExperimentVariant = getExperimentVariant('MonthlyCheckoutHowExperiment', quote?.serverSideAllocation)

  useEffect(() => {
    if (isRefundAtRenewal) {
      DataLayer.customEvent({
        eventName: `bmmb how experiment - ${bmmbHowExperimentVariant}`,
        eventType: 'form tracking',
        section: 'how',
      })
    } else {
      DataLayer.customEvent({
        eventName: `monthly how experiment - ${monthlyHowExperimentVariant}`,
        eventType: 'form tracking',
        section: 'how',
      })
    }
  }, [])

  const showExplainer =
    (bmmbHowExperimentVariant === 'variant1' && isRefundAtRenewal) ||
    (monthlyHowExperimentVariant && !isRefundAtRenewal)

  return (
    <>
      <HowStyles>
        <Hero className="hero-heading LoNotSensitive">How does it work?</Hero>
        {showExplainer ? (
          <>
            <ExplainerModalSection />
            <SectionTitle
              title={
                isRefundAtRenewal
                  ? 'The less you drive, the bigger your refund.'
                  : 'The less you drive, the less you pay.'
              }
            />
            <LottieAnimationExplainer isRefundAtRenewal={isRefundAtRenewal} />
          </>
        ) : (
          <>
            <PMBCarouselExplainer />
            <PayByMileExplainer isRefundAtRenewal={isRefundAtRenewal} />
          </>
        )}
        <SectionTitle title={state.isTrackerless ? 'Simple set up with our Connect policy.' : 'Our Miles Tracker.'} />
        <ParagraphLead style={{ color: theme.primaryColours.aubergine.main }} className="LoNotSensitive">
          {state.isTrackerless
            ? 'No device needed, you can connect us directly with your car’s mileometer over the internet.'
            : 'Simple set up. No driver scoring, no curfews. We’re not big brother.'}
        </ParagraphLead>
        <div id="breakdown-price">
          <MilesTracker />
        </div>
        <AppFeatures isTrackerless={isTrackerless} />
        <div className="get-in-touch-wrapper">
          <SectionTitle title="Get in touch." />
          <ContactUsWrapper />
        </div>
      </HowStyles>
      {state.showModal && state.activeModal === 'driving-data' && (
        <GenericOverlay
          overlay={
            <BasicOverlay
              heading="How do we use your driving data?"
              content={
                <ParagraphBody>
                  While we'd always encourage motorists to drive safely, responsibly and within the speed limit, we
                  don't change your premium based on the way you drive, where you drive, when you drive, or the speed
                  you drive at. We only use the information you give us when you start your policy and the miles you
                  travel to work out your car insurance premiums.
                  <br />
                  <br />
                  <strong>Your data is safe and secure with us.</strong>
                  <br />
                  We use information from your Miles Tracker (or connected car if you’re connecting to us using the
                  internet) to manage your insurance policy and better understand how and when accidents occur, but
                  we'll never share it with anyone else without your permission. We'll never share any information about
                  your driving with the police either, unless we're specifically asked to as a result of a court order.
                </ParagraphBody>
              }
              moreRoom={true}
            />
          }
        />
      )}
      {state.showModal && state.activeModal === 'difficult-plugin' && <DifficultPlugin nextPage="/confirm" />}

      {state.showModal && state.activeModal === 'tracker-plugin' && (
        <GenericOverlay overlay={<PluginTrackerOverlay />} />
      )}
      {showModal && activeModal === 'plugin' && (
        <WhyPluginOverlay isTrackerless={isTrackerless} inceptionDate={quote?.details.policy?.inception_date} />
      )}
    </>
  )
}

export default BasePage(How)
